import { z } from "zod";

const requiredText = (max: number) =>
  z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().min(1).max(max));

const optionalMapUrl = z
  .union([z.string(), z.null()])
  .transform((value) => {
    if (value === null) return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  })
  .pipe(
    z.union([
      z.null(),
      z
        .string()
        .max(500)
        .regex(/^https?:\/\/\S+$/),
    ]),
  );

/**
 * Public restaurant profile. One location. Country is Germany in v1.
 * Coordinates are both present or both empty.
 */
export const RestaurantProfileSchema = z
  .object({
    description: requiredText(280),
    street: requiredText(120),
    houseNumber: requiredText(20),
    postalCode: z
      .string()
      .transform((value) => value.trim())
      .pipe(z.string().regex(/^\d{5}$/)),
    city: requiredText(80),
    countryCode: z.literal("DE"),
    phone: z
      .string()
      .transform((value) => value.trim())
      .pipe(z.string().min(1).max(32).regex(/\d/)),
    email: z
      .string()
      .transform((value) => value.trim().toLowerCase())
      .pipe(z.string().email().max(254)),
    latitude: z.number().gte(-90).lte(90).nullable(),
    longitude: z.number().gte(-180).lte(180).nullable(),
    mapUrl: optionalMapUrl,
  })
  .superRefine((value, ctx) => {
    const pairEmpty = value.latitude === null && value.longitude === null;
    const pairSet = value.latitude !== null && value.longitude !== null;
    if (!pairEmpty && !pairSet) {
      ctx.addIssue({ code: "custom", message: "Coordinates must both be set" });
    }
  });

export const DashboardRestaurantProfileSchema = z.object({
  restaurantId: z.string().uuid(),
  profile: RestaurantProfileSchema.nullable(),
});

export const ReplaceRestaurantProfileBodySchema = RestaurantProfileSchema;

export type RestaurantProfile = z.infer<typeof RestaurantProfileSchema>;
export type DashboardRestaurantProfile = z.infer<typeof DashboardRestaurantProfileSchema>;
export type ReplaceRestaurantProfileBody = z.infer<typeof ReplaceRestaurantProfileBodySchema>;
